/**
 * `useAnalysisInnerState` — discriminated union for the B-13 inner state machine.
 *
 * Pure function (no side effects). Derives one of 5 inner rendering states
 * from the `useInterviewAnalysis` query result and the `useAnalysisPasteBox`
 * cache.
 *
 * Design ref: `frontend/docs/batch_b13_design.md` §2.
 * B-13.3 amendment: `transcript-loaded` state REMOVED (6 → 5 states).
 * B-13.3.1 amendment: `submitPending` added — instant Generating render on submit.
 * B-13.3.2 amendment: `analysis-loaded` now also resolves from `pasteBoxRow.analysis`
 *   — fixes the revert-to-empty bug where `submitPending` dropped to false after
 *   `onSuccess` but `analysisQuery` had not yet refetched the newly-complete row.
 *
 * Inner states (B-13.3 → auto-eval-prep: 4 states):
 *   loading              → `useInterviewAnalysis.isLoading` is true
 *   generating           → submit mutation in flight (B-13.3.1), OR row exists
 *                          with status ∈ {queued, running}, no prior analysis
 *   empty                → no transcript AND no analysis; falls through to outer machine
 *   analysis-loaded      → pasteBoxRow.analysis non-empty (B-13.3.2 primary path),
 *                          OR analysisQuery row.status === "complete" AND row.analysis
 *
 * **transcript-loaded REMOVED (B-13.3):**
 * The old "transcript present, no analysis" state is gone. The new single-action
 * submit path (`useSubmitTranscript`) runs save + parse in one round-trip, so
 * a row with transcript but no analysis is unreachable from any UI affordance.
 * If such a row exists in the DB (legacy or edge case), we map it to `generating`
 * (optimistic: the parse is in flight) rather than a dead-end terminal state.
 *
 * **analysis-regenerating REMOVED (auto-eval-prep):**
 * The analysis artifact is deterministic. `regenPending` param and the
 * `analysis-regenerating` state are gone. Row status ∈ {queued, running} with
 * a prior analysis now maps to `generating` (same as no-prior-analysis case).
 *
 * `submitPending` (B-13.3.1): `submitMutation.isPending` — the window between
 * the user hitting submit and the server response returning. Without this,
 * the state machine stays at `empty` for the entire AI roundtrip (30s+)
 * because neither `analysisQuery.data` nor `pasteBoxQuery.data` has updated
 * yet (the cache is only updated in `onSuccess`). With this flag, any submit
 * instantly transitions to `generating`.
 *
 * **B-13.3.2 revert-to-empty root cause + fix:**
 * The single-action submit endpoint runs AI parse synchronously and returns the
 * `AnalysisPasteBoxRow` with `analysis` populated in the same response. `onSuccess`
 * writes this to the paste-box cache (`["interview-analysis","paste-box",...]`).
 * At that moment, `submitPending` drops to `false`. The `analysisQuery` (which reads
 * from the DIFFERENT `["interview-analysis","by-resource",...]` key) has NOT been
 * refetched yet — it still holds `null` or a stale value. The old code only checked
 * `pasteBoxRow` in the `hasTranscriptNoAnalysis` path (which requires `!analysis`),
 * so a populated `pasteBoxRow.analysis` was invisible to the state machine. The fix:
 * check `pasteBoxRow.analysis` directly for `analysis-loaded` before the empty fallthrough.
 *
 * INVARIANT (B-13.3.2): once `pasteBoxRow.analysis` is populated by a successful submit,
 * the state machine NEVER returns `empty`. Terminal states post-submit are:
 *   - `generating` (while submit is in flight — submitPending=true)
 *   - `analysis-loaded` (when pasteBoxRow.analysis is set, i.e. after onSuccess)
 */
import type { UseQueryResult } from "@tanstack/react-query";
import type { InterviewAnalysisRow } from "./useInterviewAnalysis";
import type { AnalysisPasteBoxRow } from "./useAnalysisPasteBox";
import type { ApiError } from "@/lib/apiError";

/**
 * Minimal data shape consumed by `AnalysisLoadedView` and `analysis-regenerating`
 * renders. Both `InterviewAnalysisRow` and `AnalysisPasteBoxRow` satisfy this.
 * Using a minimal interface avoids unsafe casts between the two row types.
 */
interface AnalysisLoadedData {
  analysis: string;
  transcript: string | null | undefined;
  /**
   * Row id — feeds the `AiRatingWidget` resourceKey (`interview_analysis:${id}`).
   * Both `InterviewAnalysisRow` and `AnalysisPasteBoxRow` carry `id`.
   */
  id: string;
  /**
   * Model that produced this analysis — gates the `AiRatingWidget` (it renders
   * nothing when null). Present on `InterviewAnalysisRow`; the paste-box row does
   * not carry it, so the freshly-submitted path is `null` until `analysisQuery`
   * refetches the complete row (graceful: widget appears once the model is known).
   */
  modelUsed: string | null;
}

export type AnalysisInnerState =
  | { kind: "loading" }
  | { kind: "generating" }
  | { kind: "empty" }
  | { kind: "analysis-loaded"; row: AnalysisLoadedData };

interface UseAnalysisInnerStateOpts {
  analysisQuery: UseQueryResult<InterviewAnalysisRow | null, ApiError>;
  pasteBoxQuery: UseQueryResult<AnalysisPasteBoxRow | null, ApiError>;
  /**
   * B-13.3.1: True when `submitMutation.isPending`.
   * Guarantees the Generating state renders IMMEDIATELY on submit — before the
   * server responds — so the user never sees the dropzone hanging.
   */
  submitPending?: boolean;
}

/**
 * Derive the current inner rendering state as a discriminated union.
 * Snapshot-testable pure derivation (call site memoizes).
 *
 * B-13.3: `transcript-loaded` state removed. Legacy rows with transcript
 * but no analysis map to `generating` (optimistic; never a terminal dead-end).
 * B-13.3.1: `submitPending` added — instant Generating render on submit.
 * B-13.3.2: `pasteBoxRow.analysis` checked for `analysis-loaded` — fixes
 *   revert-to-empty when `submitPending` drops on `onSuccess` before
 *   `analysisQuery` has refetched the completed row.
 */
export function useAnalysisInnerState({
  analysisQuery,
  pasteBoxQuery,
  submitPending = false,
}: UseAnalysisInnerStateOpts): AnalysisInnerState {
  const row = analysisQuery.data ?? null;
  const pasteBoxRow = pasteBoxQuery.data ?? null;

  // Loading — the query has never resolved.
  if (analysisQuery.isLoading) {
    return { kind: "loading" };
  }

  // B-13.3.1: Submit in flight — show Generating immediately, before the server
  // responds. This is the fix for B1/B2/B3: drop/browse/paste → instant feedback.
  // Check this BEFORE the isInFlight check because the analysis row does not exist
  // yet at this point (the backend creates it during the mutation).
  if (submitPending && !row?.analysis) {
    return { kind: "generating" };
  }

  const isInFlight =
    row !== null &&
    (row.status === "queued" || row.status === "running");

  // Generating: in-flight (regardless of whether there was a prior analysis).
  // analysis-regenerating state removed (auto-eval-prep): the artifact is
  // deterministic; any re-processing simply shows the generating surface.
  if (isInFlight) {
    return { kind: "generating" };
  }

  // Analysis-loaded (primary path — analysisQuery): complete row with non-empty analysis.
  if (row && row.status === "complete" && row.analysis) {
    return {
      kind: "analysis-loaded",
      row: {
        analysis: row.analysis,
        transcript: row.transcript,
        id: row.id,
        modelUsed: row.model_used,
      },
    };
  }

  // B-13.3.2: Analysis-loaded (paste-box path).
  // The single-action submit endpoint returns the analysis synchronously. `onSuccess`
  // writes the response to the paste-box cache. At that point `submitPending` has
  // already dropped to false, but `analysisQuery` (a separate cache key) has NOT
  // yet refetched. Without this check, `pasteBoxRow.analysis` is truthy but the
  // state falls through to `empty` — the revert-to-empty bug.
  //
  // INVARIANT: once a successful submit has written `pasteBoxRow.analysis`, the
  // state machine NEVER returns `empty`. The only terminal states post-submit are
  // `generating` (AI still working) and `analysis-loaded` (AI complete).
  if (pasteBoxRow?.analysis) {
    return {
      kind: "analysis-loaded",
      row: {
        analysis: pasteBoxRow.analysis,
        transcript: pasteBoxRow.transcript,
        id: pasteBoxRow.id,
        // Paste-box row carries no model_used → widget stays hidden on the
        // freshly-submitted path until analysisQuery refetches the complete row.
        modelUsed: null,
      },
    };
  }

  // B-13.3: transcript-loaded REMOVED.
  // Legacy case: complete row with transcript but no analysis (pre-B-13.3 rows).
  // Map to `generating` — optimistic; never a terminal dead-end.
  // The paste-box cache path (pasteBoxRow with transcript but no analysis) also
  // maps here so submit-in-flight shows the spinner correctly.
  const hasTranscriptNoAnalysis =
    (row?.transcript && !row.analysis) ||
    (pasteBoxRow?.transcript &&
      !pasteBoxRow.analysis &&
      submitMutationIsLikelyPending(pasteBoxRow));
  if (hasTranscriptNoAnalysis) {
    return { kind: "generating" };
  }

  // Empty: no transcript, no analysis.
  return { kind: "empty" };
}

/**
 * Heuristic: if the paste-box row has a transcript but no analysis AND was
 * just written (saved_at within the last 60 s), treat as generating.
 * This bridges the window between useSubmitTranscript setting the cache
 * (transcript present) and the AI parse completing (analysis populated).
 */
function submitMutationIsLikelyPending(pasteBoxRow: AnalysisPasteBoxRow): boolean {
  if (!pasteBoxRow.saved_at) return false;
  const savedMs = new Date(pasteBoxRow.saved_at).getTime();
  return Date.now() - savedMs < 60_000;
}
